export interface ProductSpec {
  label: string;
  value: string;
  icon?: string;
}

export interface ProductFeature {
  title: string;
  description: string;
}

export interface ProductVariant {
  id: string;
  name: string;
  specs: ProductSpec[];
  priceIndicator?: "standard" | "premium" | "enterprise";
  image?: string;
}

export interface ProductLine {
  id: string;
  name: string;
  tagline: string;
  description: string;
  brand: string;
  category: "commercial" | "industrial" | "personal-mobility";
  productType?: "euc" | "scooter" | "ebike" | "utility" | "patrol";
  powerType: "electric" | "gas" | "hybrid";
  image: string;
  gallery: string[];
  variants: ProductVariant[];
  commonFeatures: ProductFeature[];
  availableUseCases: string[];
  colors?: string[];
  catalogUrl?: string;
  seoDescription?: string;
  seoKeywords?: string;
  customized?: boolean;
}

export interface ProductTypeFilter {
  id: string;
  name: string;
  icon: string;
  description: string;
}

export interface UseCasePackage {
  id: string;
  name: string;
  useCase: string;
  applicableProducts: string[];
  equipment: { name: string; description: string; icon?: string }[];
  additionalSpecs: ProductSpec[];
}

export interface ProductCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  gradient: string;
}

export const productCategories: ProductCategory[] = [
  {
    id: "security-patrol",
    name: "Security & Patrol",
    description: "Purpose-built vehicles for law enforcement, security forces, and patrol operations",
    icon: "Shield",
    gradient: "from-blue-600 to-blue-900",
  },
  {
    id: "medical-emergency",
    name: "Medical & Emergency",
    description: "Rapid response vehicles designed for healthcare and emergency medical services",
    icon: "HeartPulse",
    gradient: "from-red-500 to-rose-700",
  },
  {
    id: "logistics-utility",
    name: "Logistics & Utility",
    description: "Versatile workhorses for deliveries, maintenance, and industrial operations",
    icon: "Package",
    gradient: "from-amber-500 to-orange-600",
  },
  {
    id: "events-hospitality",
    name: "Events & Hospitality",
    description: "Premium mobility solutions for venues, resorts, and large-scale events",
    icon: "Sparkles",
    gradient: "from-purple-500 to-violet-700",
  },
  {
    id: "personal-mobility",
    name: "Personal Mobility",
    description: "Compact, efficient vehicles for individual transportation and urban commuting",
    icon: "Zap",
    gradient: "from-eco-green to-emerald-600",
  },
];

// Product type filters for King Song personal mobility
export const productTypeFilters: ProductTypeFilter[] = [
  { id: "all", name: "All Products", icon: "Grid3X3", description: "View all personal mobility products" },
  { id: "euc", name: "Electric Unicycles", icon: "Circle", description: "Self-balancing one-wheel vehicles" },
  { id: "scooter", name: "Electric Scooters", icon: "Zap", description: "Standing electric kick scooters" },
  { id: "ebike", name: "E-Bikes", icon: "Bike", description: "Electric bicycles and mopeds" },
];

export const productLines: ProductLine[] = [
  // ==========================================
  // COMMERCIAL / B2B MOBILITY (Doohan & XSTO)
  // ==========================================
  
  // iTank - Main product with Single and Dual Battery variants
  {
    id: "itank",
    name: "iTank",
    tagline: "Power Meets Stability",
    description: "The flagship 3-wheeled electric vehicle engineered for maximum stability and performance. Available in Single Battery for standard operations or Dual Battery for extended range missions.",
    brand: "Doohan",
    category: "commercial",
    productType: "patrol",
    powerType: "electric",
    image: "/products/itank/itank-left-side.png",
    gallery: [
      "/products/itank/itank-left-side.png",
      "/products/itank/itank-front.jpg",
      "/products/itank/itank-back.jpg",
      "/products/itank/itank-top.jpg",
      "/products/itank/itank-hero.png",
    ],
    colors: ["White"],
    catalogUrl: "/products/catalogs/itank-police-edition.pdf",
    seoDescription: "iTank electric patrol vehicle with 3000W motor, 90 km/h top speed, and up to 140km range. Available in Single and Dual Battery configurations for security and patrol operations.",
    seoKeywords: "iTank, electric patrol vehicle, 3-wheel EV, security vehicle, Doohan, SDC Gulf",
    variants: [
      {
        id: "itank-single",
        name: "Single Battery",
        priceIndicator: "standard",
        specs: [
          { label: "Motor Power", value: "3000W", icon: "Zap" },
          { label: "Max Speed", value: "90 km/h", icon: "Gauge" },
          { label: "Max Range", value: "70 km", icon: "Route" },
          { label: "Battery", value: "60V/26Ah", icon: "Battery" },
          { label: "Max Load", value: "160 kg", icon: "Weight" },
          { label: "Wheel Config", value: "3-Wheel", icon: "Circle" },
          { label: "Charging Time", value: "4-6 hrs", icon: "Clock" },
        ],
      },
      {
        id: "itank-dual",
        name: "Dual Battery",
        priceIndicator: "premium",
        specs: [
          { label: "Motor Power", value: "3000W", icon: "Zap" },
          { label: "Max Speed", value: "90 km/h", icon: "Gauge" },
          { label: "Max Range", value: "140 km", icon: "Route" },
          { label: "Battery", value: "60V/26Ah x2", icon: "Battery" },
          { label: "Max Load", value: "160 kg", icon: "Weight" },
          { label: "Wheel Config", value: "3-Wheel", icon: "Circle" },
          { label: "Charging Time", value: "4-6 hrs", icon: "Clock" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Waterproof LCD Display", description: "High-visibility digital dashboard readable in all conditions" },
      { title: "LED Headlights", description: "High and low beam adaptive LED lighting system" },
      { title: "Safe & Stable", description: "3-wheel design provides superior stability on any terrain" },
      { title: "Swappable Battery", description: "Quick battery swap for zero downtime operations" },
    ],
    availableUseCases: ["security-patrol", "medical-emergency", "logistics-utility"],
  },
  // iTango - Urban commuter with Classic and H3 variants
  {
    id: "itango",
    name: "iTango",
    tagline: "Urban Agility Redefined",
    description: "The nimble 3-wheeled electric scooter with 1200W BOSCH motor, perfect for urban navigation and last-mile logistics. Available in Classic for standard range or H3 for extended range up to 130km.",
    brand: "Doohan",
    category: "commercial",
    productType: "scooter",
    powerType: "electric",
    image: "/products/itango/itango-left-side.png",
    gallery: [
      "/products/itango/itango-left-side.png",
      "/products/itango/itango-angle-1.png",
      "/products/itango/itango-front.jpg",
      "/products/itango/itango-rear.jpg",
      "/products/itango/itango-dashboard.jpg",
      "/products/itango/itango-angle-2.png",
    ],
    colors: ["Red", "Black", "White", "Green", "Blue"],
    seoDescription: "iTango electric 3-wheel scooter with 1200W BOSCH motor and up to 130km range. Available in Classic and H3 variants for urban mobility and last-mile logistics.",
    seoKeywords: "iTango, electric scooter, 3-wheel, urban mobility, Doohan, BOSCH motor, SDC Gulf",
    variants: [
      {
        id: "itango-classic",
        name: "Classic",
        priceIndicator: "standard",
        specs: [
          { label: "Motor Power", value: "1200W BOSCH", icon: "Zap" },
          { label: "Max Speed", value: "25 km/h", icon: "Gauge" },
          { label: "Max Range", value: "65 km", icon: "Route" },
          { label: "Battery", value: "48V/20Ah", icon: "Battery" },
          { label: "Front Track", value: "460mm", icon: "Ruler" },
          { label: "Drop Height", value: "160mm", icon: "ArrowDown" },
          { label: "Leaning Angle", value: "30°", icon: "RotateCcw" },
          { label: "Steering Angle", value: "34°", icon: "Navigation" },
        ],
      },
      {
        id: "itango-h3",
        name: "H3",
        priceIndicator: "premium",
        specs: [
          { label: "Motor Power", value: "1200W BOSCH", icon: "Zap" },
          { label: "Max Speed", value: "25 km/h", icon: "Gauge" },
          { label: "Max Range", value: "130 km", icon: "Route" },
          { label: "Battery", value: "48V/20Ah x2", icon: "Battery" },
          { label: "Front Track", value: "460mm", icon: "Ruler" },
          { label: "Drop Height", value: "160mm", icon: "ArrowDown" },
          { label: "Leaning Angle", value: "30°", icon: "RotateCcw" },
          { label: "Steering Angle", value: "34°", icon: "Navigation" },
        ],
      },
    ],
    commonFeatures: [
      { title: "BOSCH Motor", description: "Premium German engineering for reliable performance" },
      { title: "Auto-Adapting LED", description: "Headlights automatically adjust to lighting conditions" },
      { title: "Daylight Visibility", description: "Clear LCD display visible in direct sunlight" },
      { title: "Safe & Stable", description: "Low center of gravity for balanced handling" },
    ],
    availableUseCases: ["logistics-utility", "personal-mobility"],
  },
  // iLark - Personal mobility scooter with 180W and 300W variants
  {
    id: "ilark",
    name: "iLark",
    tagline: "Fold. Go. Explore.",
    description: "The ultra-compact folding electric scooter designed for maximum portability. Powered by BOSCH motor with IP54 water resistance, perfect for venues, events, and personal transport.",
    brand: "Doohan",
    category: "commercial",
    productType: "scooter",
    powerType: "electric",
    image: "/products/ilark/ilark-left-side.png",
    gallery: [
      "/products/ilark/ilark-left-side.png",
      "/products/ilark/ilark-black-angle.png",
      "/products/ilark/ilark-black-side.png",
      "/products/ilark/ilark-lifestyle-1.png",
      "/products/ilark/ilark-lifestyle-2.jpg",
      "/products/ilark/ilark-lifestyle-3.jpg",
      "/products/ilark/ilark-lifestyle-4.jpg",
    ],
    colors: ["White", "Black"],
    seoDescription: "iLark folding electric scooter with BOSCH motor and IP54 water resistance. Available in 180W and 300W variants for personal mobility and venue transport.",
    seoKeywords: "iLark, folding scooter, electric scooter, BOSCH motor, portable, Doohan, SDC Gulf",
    variants: [
      {
        id: "ilark-180w",
        name: "180W",
        priceIndicator: "standard",
        specs: [
          { label: "Motor Power", value: "180W BOSCH", icon: "Zap" },
          { label: "Max Speed", value: "25 km/h", icon: "Gauge" },
          { label: "Max Range", value: "25 km", icon: "Route" },
          { label: "Battery", value: "48V/10Ah", icon: "Battery" },
          { label: "Dimensions", value: "1125×513×1100mm", icon: "Ruler" },
          { label: "Wheel Base", value: "800mm", icon: "Ruler" },
          { label: "Curb Weight", value: "28 kg", icon: "Weight" },
          { label: "Max Load", value: "100 kg", icon: "Weight" },
          { label: "Charge Time", value: "3 hrs", icon: "Clock" },
          { label: "Water Resist", value: "IP54", icon: "Droplet" },
        ],
      },
      {
        id: "ilark-300w",
        name: "300W",
        priceIndicator: "premium",
        specs: [
          { label: "Motor Power", value: "300W BOSCH", icon: "Zap" },
          { label: "Max Speed", value: "25 km/h", icon: "Gauge" },
          { label: "Max Range", value: "35 km", icon: "Route" },
          { label: "Battery", value: "48V/15Ah", icon: "Battery" },
          { label: "Dimensions", value: "1125×513×1100mm", icon: "Ruler" },
          { label: "Wheel Base", value: "800mm", icon: "Ruler" },
          { label: "Curb Weight", value: "29 kg", icon: "Weight" },
          { label: "Max Load", value: "100 kg", icon: "Weight" },
          { label: "Charge Time", value: "5.5 hrs", icon: "Clock" },
          { label: "Water Resist", value: "IP54", icon: "Droplet" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Foldable Handlebars", description: "Fold down for easy storage and transport" },
      { title: "Adjustable Seat Height", description: "Customize riding position for comfort" },
      { title: "Waterproof LCD", description: "High brightness display for all conditions" },
      { title: "Compact Design", description: "Minimal footprint for tight spaces" },
    ],
    availableUseCases: ["events-hospitality", "personal-mobility"],
  },
  // ET300P2 - Utility vehicle
  {
    id: "icart",
    name: "ET300P2",
    tagline: "The Workhorse",
    description: "Heavy-duty electric utility platform designed for cargo transport, facility management, and industrial operations. Built for durability and maximum payload capacity with detachable power module.",
    brand: "XSTO",
    category: "industrial",
    productType: "utility",
    powerType: "electric",
    image: "/products/xsto/et300p2-angle.png",
    gallery: [
      "/products/xsto/et300p2-angle.png",
      "/products/icart/et300p2-side.png",
      "/products/icart/et300p2-top.png",
      "/products/icart/et300p2-detail.png",
    ],
    colors: ["Black"],
    seoDescription: "ET300P2 heavy-duty electric utility platform with 300kg payload capacity and 6-wheel configuration. Perfect for cargo transport and facility management.",
    seoKeywords: "ET300P2, utility vehicle, electric cart, cargo transport, XSTO, SDC Gulf",
    variants: [
      {
        id: "icart-standard",
        name: "Standard",
        priceIndicator: "standard",
        specs: [
          { label: "Motor Power", value: "2000W", icon: "Zap" },
          { label: "Max Speed", value: "35 km/h", icon: "Gauge" },
          { label: "Max Range", value: "60 km", icon: "Route" },
          { label: "Battery", value: "72V/40Ah", icon: "Battery" },
          { label: "Max Load", value: "300 kg", icon: "Weight" },
          { label: "Wheel Config", value: "6-Wheel", icon: "Circle" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Detachable Power Module", description: "Removable self-balancing power unit for versatile operation" },
      { title: "Heavy Duty Platform", description: "Reinforced flatbed for transporting equipment and supplies" },
      { title: "Foldable Handle", description: "Collapsible handle for compact storage" },
      { title: "Easy Loading", description: "Low bed height for convenient loading and unloading" },
    ],
    availableUseCases: ["logistics-utility", "events-hospitality"],
  },
  // ADV 150CC - Gas patrol scooter
  {
    id: "adv-150cc",
    name: "ADV 150CC",
    tagline: "Customized for Duty",
    description: "Water-cooled gas-powered patrol scooter with 10.2KW power and 95km/h top speed. Fully customized by Sustainable Development Co. with professional security equipment.",
    brand: "Custom",
    category: "commercial",
    productType: "patrol",
    powerType: "gas",
    image: "/products/adv-150cc.png",
    gallery: ["/products/adv-150cc.png"],
    seoDescription: "ADV 150CC gas-powered patrol scooter with 10.2KW power and 95km/h top speed. Customized for law enforcement and security patrol operations.",
    seoKeywords: "ADV 150CC, gas scooter, patrol vehicle, security, law enforcement, SDC Gulf",
    variants: [
      {
        id: "adv-150cc-standard",
        name: "Standard",
        priceIndicator: "standard",
        specs: [
          { label: "Max Power", value: "10.2KW", icon: "Zap" },
          { label: "Max Speed", value: "95 km/h", icon: "Gauge" },
          { label: "Engine", value: "150CC", icon: "Settings" },
          { label: "Cooling", value: "Water-Cooled", icon: "Droplet" },
          { label: "Front Tire", value: "110/80-14", icon: "Circle" },
          { label: "Rear Tire", value: "130/70-13", icon: "Circle" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Digital Dashboard", description: "Full-color LCD with speed, fuel, and diagnostics" },
      { title: "Adaptive Shock Absorption", description: "Premium suspension for smooth patrol rides" },
      { title: "Powerful Speaker System", description: "Integrated PA with siren and announcement capability" },
      { title: "LED Front & Rear Lights", description: "High-visibility lighting with integrated siren speaker" },
    ],
    availableUseCases: ["security-patrol"],
    customized: true,
  },
  
  // XSTO Stair Climbers & Electric Hand Trucks
  {
    id: "xsto-zw47250",
    name: "ZW47250",
    tagline: "550lbs Motorized Stair Climber",
    description: "Professional-grade motorized stair climbing hand truck with 550lbs capacity. Made of aluminum alloy and carbon steel, lightweight yet powerful for easy stair navigation.",
    brand: "XSTO",
    category: "industrial",
    productType: "utility",
    powerType: "electric",
    image: "/products/xsto/zw47250.webp",
    gallery: ["/products/xsto/zw47250.webp"],
    colors: ["Blue/Black"],
    seoDescription: "XSTO ZW47250 motorized stair climbing hand truck with 550lbs capacity. Aluminum alloy construction for professional cargo transport on stairs.",
    seoKeywords: "XSTO, ZW47250, stair climber, hand truck, motorized dolly, SDC Gulf",
    variants: [
      {
        id: "xsto-zw47250-standard",
        name: "Standard",
        priceIndicator: "standard",
        specs: [
          { label: "Max Load", value: "550 lbs (250kg)", icon: "Weight" },
          { label: "Battery", value: "48V Lithium", icon: "Battery" },
          { label: "Material", value: "Aluminum Alloy", icon: "Settings" },
          { label: "Wheel Type", value: "Tri-Wheel", icon: "Circle" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Tri-Wheel System", description: "Smooth stair climbing with rotating tri-wheel mechanism" },
      { title: "Lightweight Frame", description: "Aluminum alloy construction for easy handling" },
      { title: "Variable Speed", description: "Adjustable speed control for different loads and stairs" },
      { title: "Safety Brakes", description: "Automatic braking system for secure stopping" },
    ],
    availableUseCases: ["logistics-utility"],
  },
  {
    id: "xsto-ct420sc",
    name: "CT420SC",
    tagline: "925lbs Heavy-Duty Stair Climber",
    description: "Industrial-strength electric stair climbing trolley with 925lbs maximum capacity. Single operator can complete load transport up and down stairs through intuitive joystick control.",
    brand: "XSTO",
    category: "industrial",
    productType: "utility",
    powerType: "electric",
    image: "/products/xsto/ct420sc.webp",
    gallery: ["/products/xsto/ct420sc.webp"],
    colors: ["Blue/Black"],
    seoDescription: "XSTO CT420SC heavy-duty electric stair climbing trolley with 925lbs capacity. Industrial-grade for transporting heavy equipment on stairs.",
    seoKeywords: "XSTO, CT420SC, heavy duty, stair climber, industrial trolley, SDC Gulf",
    variants: [
      {
        id: "xsto-ct420sc-standard",
        name: "Standard",
        priceIndicator: "enterprise",
        specs: [
          { label: "Max Load", value: "925 lbs (420kg)", icon: "Weight" },
          { label: "Battery", value: "48V Lithium", icon: "Battery" },
          { label: "Control", value: "Joystick", icon: "Settings" },
          { label: "Operation", value: "Single Operator", icon: "Circle" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Joystick Control", description: "Intuitive single-hand operation for precise movement" },
      { title: "Heavy-Duty Frame", description: "Industrial-grade construction for maximum durability" },
      { title: "Self-Balancing", description: "Automatic load balancing for safe stair transit" },
      { title: "All-Terrain Wheels", description: "Rubber wheels for grip on all surfaces" },
    ],
    availableUseCases: ["logistics-utility"],
  },
  {
    id: "xsto-zw7250gc",
    name: "ZW7250GC",
    tagline: "550lbs Powered Stair Climber",
    description: "Advanced powered stair climbing hand truck with edge brake technology. 550lbs capacity with enhanced safety features for professional moving and delivery operations.",
    brand: "XSTO",
    category: "industrial",
    productType: "utility",
    powerType: "electric",
    image: "/products/xsto/zw7250gc.webp",
    gallery: ["/products/xsto/zw7250gc.webp"],
    colors: ["Blue/Black"],
    seoDescription: "XSTO ZW7250GC powered stair climber with edge brake technology and 550lbs capacity for safe cargo transport.",
    seoKeywords: "XSTO, ZW7250GC, stair climber, edge brake, powered dolly, SDC Gulf",
    variants: [
      {
        id: "xsto-zw7250gc-standard",
        name: "Standard",
        priceIndicator: "premium",
        specs: [
          { label: "Max Load", value: "550 lbs (250kg)", icon: "Weight" },
          { label: "Battery", value: "48V Lithium", icon: "Battery" },
          { label: "Brake System", value: "Edge Brake", icon: "Settings" },
          { label: "Material", value: "Carbon Steel", icon: "Circle" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Edge Brake Technology", description: "Automatic edge detection and braking for safety" },
      { title: "Carbon Steel Frame", description: "Heavy-duty construction for demanding use" },
      { title: "Ergonomic Handle", description: "Comfortable grip for extended operation" },
      { title: "Quick Release", description: "Fast battery swap for continuous operation" },
    ],
    availableUseCases: ["logistics-utility"],
  },
  {
    id: "xsto-scl170ac",
    name: "SCL170AC",
    tagline: "375lbs All-in-One Stair Climber",
    description: "Multi-function motorized stair climber with integrated forklift capability. Combines stair climbing, lifting, loading, and unloading functions for complete warehouse and delivery solutions.",
    brand: "XSTO",
    category: "industrial",
    productType: "utility",
    powerType: "electric",
    image: "/products/xsto/scl170ac.png",
    gallery: ["/products/xsto/scl170ac.png"],
    colors: ["Blue/Black"],
    seoDescription: "XSTO SCL170AC all-in-one motorized stair climber with forklift functionality. Multi-purpose for warehouse and delivery operations.",
    seoKeywords: "XSTO, SCL170AC, stair climber, forklift, all-in-one, SDC Gulf",
    variants: [
      {
        id: "xsto-scl170ac-standard",
        name: "Standard",
        priceIndicator: "premium",
        specs: [
          { label: "Max Load", value: "375 lbs (170kg)", icon: "Weight" },
          { label: "Battery", value: "48V Lithium", icon: "Battery" },
          { label: "Functions", value: "4-in-1", icon: "Settings" },
          { label: "Lift Type", value: "Forklift", icon: "ArrowDown" },
        ],
      },
    ],
    commonFeatures: [
      { title: "4-in-1 Functionality", description: "Stair climbing, lifting, loading, and unloading" },
      { title: "Forklift Integration", description: "Built-in lifting mechanism for pallet handling" },
      { title: "Self-Lifting", description: "Convenient loading and unloading capability" },
      { title: "Compact Design", description: "Maneuverable in tight warehouse spaces" },
    ],
    availableUseCases: ["logistics-utility"],
  },
  {
    id: "xsto-ct150pb",
    name: "CT150PB",
    tagline: "331lbs Electric Stair Climber",
    description: "Compact electric stair climber designed for medium-duty applications. Perfect balance of power and portability for delivery, moving, and facility operations.",
    brand: "XSTO",
    category: "industrial",
    productType: "utility",
    powerType: "electric",
    image: "/products/xsto/ct150pb.png",
    gallery: ["/products/xsto/ct150pb.png"],
    colors: ["Blue/Black"],
    seoDescription: "XSTO CT150PB compact electric stair climber with 331lbs capacity for medium-duty delivery and moving applications.",
    seoKeywords: "XSTO, CT150PB, stair climber, compact, electric dolly, SDC Gulf",
    variants: [
      {
        id: "xsto-ct150pb-standard",
        name: "Standard",
        priceIndicator: "standard",
        specs: [
          { label: "Max Load", value: "331 lbs (150kg)", icon: "Weight" },
          { label: "Battery", value: "48V Lithium", icon: "Battery" },
          { label: "Weight", value: "Compact", icon: "Weight" },
          { label: "Wheel Type", value: "Tri-Wheel", icon: "Circle" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Compact Size", description: "Easy to store and transport between jobs" },
      { title: "Quick Setup", description: "Ready to use in seconds" },
      { title: "Quiet Operation", description: "Low noise for residential use" },
      { title: "LED Display", description: "Battery and speed information at a glance" },
    ],
    availableUseCases: ["logistics-utility"],
  },
  {
    id: "xsto-lfc170f3",
    name: "LFC170F3",
    tagline: "375lbs Motorized Hand Truck",
    description: "Electric motorized hand truck designed for flat ground and slope transport. Features self-lifting function for convenient loading and unloading operations.",
    brand: "XSTO",
    category: "industrial",
    productType: "utility",
    powerType: "electric",
    image: "/products/xsto/lfc170f3.webp",
    gallery: ["/products/xsto/lfc170f3.webp"],
    colors: ["Blue/Black"],
    seoDescription: "XSTO LFC170F3 motorized hand truck with 375lbs capacity and self-lifting function for flat ground and slope transport.",
    seoKeywords: "XSTO, LFC170F3, motorized hand truck, electric dolly, SDC Gulf",
    variants: [
      {
        id: "xsto-lfc170f3-standard",
        name: "Standard",
        priceIndicator: "premium",
        specs: [
          { label: "Max Load", value: "375 lbs (170kg)", icon: "Weight" },
          { label: "Battery", value: "48V Lithium", icon: "Battery" },
          { label: "Terrain", value: "Flat/Slope", icon: "Route" },
          { label: "Feature", value: "Self-Lifting", icon: "ArrowDown" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Self-Lifting", description: "Automatic lift for easy loading/unloading" },
      { title: "Slope Ready", description: "Designed for inclines and ramps" },
      { title: "Electric Drive", description: "Powered wheels reduce operator effort" },
      { title: "Ergonomic Controls", description: "Comfortable operation for extended use" },
    ],
    availableUseCases: ["logistics-utility"],
  },
  {
    id: "xsto-ct250s",
    name: "CT250S",
    tagline: "551lbs Self-Balancing Stair Climber",
    description: "Next-generation self-balancing electric stair climbing dolly with 551lbs capacity. Advanced stabilization technology ensures safe and effortless heavy load transport.",
    brand: "XSTO",
    category: "industrial",
    productType: "utility",
    powerType: "electric",
    image: "/products/xsto/ct250s.png",
    gallery: ["/products/xsto/ct250s.png"],
    colors: ["Blue/Black"],
    seoDescription: "XSTO CT250S self-balancing stair climber with 551lbs capacity. Advanced stabilization for safe heavy load transport on stairs.",
    seoKeywords: "XSTO, CT250S, self-balancing, stair climber, heavy duty, SDC Gulf",
    variants: [
      {
        id: "xsto-ct250s-standard",
        name: "Standard",
        priceIndicator: "premium",
        specs: [
          { label: "Max Load", value: "551 lbs (250kg)", icon: "Weight" },
          { label: "Battery", value: "48V Lithium", icon: "Battery" },
          { label: "Technology", value: "Self-Balancing", icon: "Settings" },
          { label: "Control", value: "Digital", icon: "Circle" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Self-Balancing", description: "Automatic load stabilization for safe transport" },
      { title: "Digital Control", description: "Precise electronic speed and direction control" },
      { title: "Heavy Capacity", description: "551lbs max load for large appliances and equipment" },
      { title: "Safety Sensors", description: "Multiple sensors prevent tip-overs" },
    ],
    availableUseCases: ["logistics-utility"],
  },
  {
    id: "xsto-zw7210gc",
    name: "ZW7210GC",
    tagline: "463lbs Motorized Stair Climber",
    description: "Professional motorized stair climbing hand truck with edge brake technology and 463lbs capacity. Perfect for appliance delivery and commercial moving operations.",
    brand: "XSTO",
    category: "industrial",
    productType: "utility",
    powerType: "electric",
    image: "/products/xsto/zw7210gc.png",
    gallery: ["/products/xsto/zw7210gc.png"],
    colors: ["Blue/Black"],
    seoDescription: "XSTO ZW7210GC motorized stair climbing hand truck with edge brake and 463lbs capacity for professional delivery operations.",
    seoKeywords: "XSTO, ZW7210GC, stair climber, edge brake, motorized hand truck, SDC Gulf",
    variants: [
      {
        id: "xsto-zw7210gc-standard",
        name: "Standard",
        priceIndicator: "premium",
        specs: [
          { label: "Max Load", value: "463 lbs (210kg)", icon: "Weight" },
          { label: "Battery", value: "48V Lithium", icon: "Battery" },
          { label: "Brake System", value: "Edge Brake", icon: "Settings" },
          { label: "Wheel Type", value: "Tri-Wheel", icon: "Circle" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Edge Brake Technology", description: "Automatic braking at stair edges for safety" },
      { title: "Tri-Wheel Climbing", description: "Smooth rotation for effortless stair navigation" },
      { title: "Adjustable Handle", description: "Height-adjustable for operator comfort" },
      { title: "Foldable Design", description: "Compact storage in delivery vehicles" },
    ],
    availableUseCases: ["logistics-utility"],
  },

  // ==========================================
  // PERSONAL MOBILITY (King Song)
  // ==========================================
  
  // King Song Electric Unicycles - Flagship/Off-Road
  {
    id: "ks-f22-pro",
    name: "F22 Pro",
    tagline: "Next-Gen Off-Road Beast",
    description: "The ultimate off-road electric unicycle featuring 5500W motor, massive 3108Wh battery, and 4-unit suspension system. 200km range and 20-inch tire for maximum stability on any terrain.",
    brand: "King Song",
    category: "personal-mobility",
    productType: "euc",
    powerType: "electric",
    image: "/products/kingsong/f22-pro.png",
    gallery: ["/products/kingsong/f22-pro.png"],
    colors: ["Black", "Orange"],
    seoDescription: "King Song F22 Pro electric unicycle with 5500W motor, 3108Wh battery, 4-unit suspension, and 200km range. Ultimate off-road performance.",
    seoKeywords: "King Song F22 Pro, electric unicycle, EUC, off-road, suspension, SDC Gulf",
    variants: [
      {
        id: "ks-f22-pro-standard",
        name: "Standard",
        priceIndicator: "premium",
        specs: [
          { label: "Peak Motor", value: "5500W", icon: "Zap" },
          { label: "Max Speed", value: "70 km/h", icon: "Gauge" },
          { label: "Max Range", value: "200 km", icon: "Route" },
          { label: "Battery", value: "3108Wh", icon: "Battery" },
          { label: "Tire Size", value: "20\"", icon: "Circle" },
          { label: "Suspension", value: "4-Unit", icon: "ArrowDown" },
        ],
      },
    ],
    commonFeatures: [
      { title: "4-Unit Suspension", description: "Advanced quad suspension for extreme terrain" },
      { title: "Samsung 50S Cells", description: "Premium high-capacity battery cells" },
      { title: "UL 2272 Certified", description: "Highest safety standards" },
      { title: "Smart BMS", description: "Intelligent battery management system" },
    ],
    availableUseCases: ["personal-mobility"],
  },
  {
    id: "ks-f18",
    name: "F18",
    tagline: "All-Terrain Champion",
    description: "Versatile 18-inch off-road electric unicycle with 5000W motor, 2664Wh Samsung battery, and 4-unit suspension. Perfect for adventure riders seeking the best suspension technology.",
    brand: "King Song",
    category: "personal-mobility",
    productType: "euc",
    powerType: "electric",
    image: "/products/kingsong/f18.png",
    gallery: ["/products/kingsong/f18.png"],
    colors: ["Black", "Orange"],
    seoDescription: "King Song F18 electric unicycle with 5000W motor, 2664Wh battery, and 4-unit suspension for off-road adventures.",
    seoKeywords: "King Song F18, electric unicycle, EUC, off-road, suspension, SDC Gulf",
    variants: [
      {
        id: "ks-f18-standard",
        name: "Standard",
        priceIndicator: "premium",
        specs: [
          { label: "Peak Motor", value: "5000W", icon: "Zap" },
          { label: "Max Speed", value: "65 km/h", icon: "Gauge" },
          { label: "Max Range", value: "180 km", icon: "Route" },
          { label: "Battery", value: "2664Wh", icon: "Battery" },
          { label: "Tire Size", value: "18\"", icon: "Circle" },
          { label: "Suspension", value: "4-Unit", icon: "ArrowDown" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Best Suspension", description: "4-unit suspension system for smooth rides" },
      { title: "Samsung 50S Cells", description: "High-capacity Samsung battery" },
      { title: "UL 2272 Certified", description: "Safety certified" },
      { title: "App Control", description: "Full customization via smartphone" },
    ],
    availableUseCases: ["personal-mobility"],
  },
  {
    id: "ks-s22-pro",
    name: "S22 Pro+",
    tagline: "Ultimate Off-Road Performance",
    description: "The pinnacle of electric unicycle engineering with 8500W peak motor, 2220Wh battery, and advanced air suspension. Built for off-road enthusiasts who demand the absolute best.",
    brand: "King Song",
    category: "personal-mobility",
    productType: "euc",
    powerType: "electric",
    image: "/products/kingsong/s22-pro.png",
    gallery: ["/products/kingsong/s22-pro.png"],
    colors: ["Black", "White"],
    seoDescription: "King Song S22 Pro+ electric unicycle with 8500W peak motor, 2220Wh battery, and advanced suspension for extreme off-road performance.",
    seoKeywords: "King Song S22 Pro, electric unicycle, EUC, off-road, personal mobility, SDC Gulf",
    variants: [
      {
        id: "ks-s22-pro-standard",
        name: "Standard",
        priceIndicator: "premium",
        specs: [
          { label: "Peak Motor", value: "8500W", icon: "Zap" },
          { label: "Max Speed", value: "70 km/h", icon: "Gauge" },
          { label: "Max Range", value: "150 km", icon: "Route" },
          { label: "Battery", value: "2220Wh", icon: "Battery" },
          { label: "Tire Size", value: "18\"", icon: "Circle" },
          { label: "Suspension", value: "Air", icon: "ArrowDown" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Air Suspension", description: "Adjustable air suspension for any terrain" },
      { title: "Smart BMS", description: "Intelligent battery management for safety and longevity" },
      { title: "UL 2272 Certified", description: "Meets highest safety standards" },
      { title: "App Control", description: "Full customization via smartphone app" },
    ],
    availableUseCases: ["personal-mobility"],
  },
  {
    id: "ks-s19-pro",
    name: "S19 Pro",
    tagline: "Power Meets Precision",
    description: "High-performance electric unicycle with 6500W peak motor and 1776Wh battery. Features advanced air suspension for smooth rides on any surface.",
    brand: "King Song",
    category: "personal-mobility",
    productType: "euc",
    powerType: "electric",
    image: "/products/kingsong/s19-pro.png",
    gallery: ["/products/kingsong/s19-pro.png"],
    colors: ["Black", "White"],
    seoDescription: "King Song S19 Pro electric unicycle with 6500W peak motor, 1776Wh battery, and air suspension for advanced riders.",
    seoKeywords: "King Song S19 Pro, electric unicycle, EUC, suspension, personal mobility, SDC Gulf",
    variants: [
      {
        id: "ks-s19-pro-standard",
        name: "Standard",
        priceIndicator: "premium",
        specs: [
          { label: "Peak Motor", value: "6500W", icon: "Zap" },
          { label: "Max Speed", value: "60 km/h", icon: "Gauge" },
          { label: "Max Range", value: "160 km", icon: "Route" },
          { label: "Battery", value: "1776Wh", icon: "Battery" },
          { label: "Tire Size", value: "18\"", icon: "Circle" },
          { label: "Suspension", value: "Air", icon: "ArrowDown" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Air Suspension", description: "Professional-grade adjustable suspension" },
      { title: "Smart BMS", description: "Advanced battery management system" },
      { title: "UL 2272 Certified", description: "Safety certified for peace of mind" },
      { title: "Quick Charging", description: "Fast charge capability for minimal downtime" },
    ],
    availableUseCases: ["personal-mobility"],
  },
  {
    id: "ks-s18-pro",
    name: "S18 Pro+",
    tagline: "Suspension Pioneer",
    description: "The original suspension electric unicycle with 5000W motor and 1110Wh battery. Perfect balance of performance and comfort for daily commuting and weekend adventures.",
    brand: "King Song",
    category: "personal-mobility",
    productType: "euc",
    powerType: "electric",
    image: "/products/kingsong/s18-pro.png",
    gallery: ["/products/kingsong/s18-pro.png"],
    colors: ["Black", "White"],
    seoDescription: "King Song S18 Pro+ suspension electric unicycle with 5000W motor and 1110Wh battery for comfortable commuting.",
    seoKeywords: "King Song S18 Pro, electric unicycle, EUC, suspension, commuter, SDC Gulf",
    variants: [
      {
        id: "ks-s18-pro-standard",
        name: "Standard",
        priceIndicator: "standard",
        specs: [
          { label: "Peak Motor", value: "5000W", icon: "Zap" },
          { label: "Max Speed", value: "50 km/h", icon: "Gauge" },
          { label: "Max Range", value: "100 km", icon: "Route" },
          { label: "Battery", value: "1110Wh", icon: "Battery" },
          { label: "Tire Size", value: "18\"", icon: "Circle" },
          { label: "Suspension", value: "Air", icon: "ArrowDown" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Air Suspension", description: "Pioneering suspension design for smooth rides" },
      { title: "Smart BMS", description: "Intelligent battery protection" },
      { title: "UL 2272 Certified", description: "Certified safe for daily use" },
      { title: "LED Lighting", description: "Bright front and rear LED lights" },
    ],
    availableUseCases: ["personal-mobility"],
  },
  // King Song 18XL Series
  {
    id: "ks-18xl-pro",
    name: "18XL Pro",
    tagline: "Beginner-Optimized Performance",
    description: "Premium 18-inch electric unicycle with 4600W motor and 1554Wh battery. Intelligent BMS and beginner-friendly features make it perfect for riders transitioning to larger wheels.",
    brand: "King Song",
    category: "personal-mobility",
    productType: "euc",
    powerType: "electric",
    image: "/products/kingsong/18xl-pro.png",
    gallery: ["/products/kingsong/18xl-pro.png"],
    colors: ["Black", "White"],
    seoDescription: "King Song 18XL Pro electric unicycle with 4600W motor, 1554Wh battery, and beginner-friendly features.",
    seoKeywords: "King Song 18XL Pro, electric unicycle, EUC, beginner, SDC Gulf",
    variants: [
      {
        id: "ks-18xl-pro-standard",
        name: "Standard",
        priceIndicator: "standard",
        specs: [
          { label: "Peak Motor", value: "4600W", icon: "Zap" },
          { label: "Max Speed", value: "50 km/h", icon: "Gauge" },
          { label: "Max Range", value: "140 km", icon: "Route" },
          { label: "Battery", value: "1554Wh", icon: "Battery" },
          { label: "Tire Size", value: "18\"", icon: "Circle" },
          { label: "Weight", value: "26 kg", icon: "Weight" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Beginner-Optimized", description: "Learning-friendly speed limits and alerts" },
      { title: "Intelligent BMS", description: "Smart battery management system" },
      { title: "UL 2272 Certified", description: "Safety certified" },
      { title: "App Control", description: "Customize settings via app" },
    ],
    availableUseCases: ["personal-mobility"],
  },
  {
    id: "ks-18xl",
    name: "18XL",
    tagline: "Long-Range Cruiser",
    description: "Classic 18-inch electric unicycle with 4000W peak power and 1554Wh battery. Excellent 140km range makes it ideal for long-distance commuting and touring.",
    brand: "King Song",
    category: "personal-mobility",
    productType: "euc",
    powerType: "electric",
    image: "/products/kingsong/18xl.png",
    gallery: ["/products/kingsong/18xl.png"],
    colors: ["Black"],
    seoDescription: "King Song 18XL electric unicycle with 4000W peak power, 1554Wh battery, and 140km range for long-distance riding.",
    seoKeywords: "King Song 18XL, electric unicycle, EUC, long range, touring, SDC Gulf",
    variants: [
      {
        id: "ks-18xl-standard",
        name: "Standard",
        priceIndicator: "standard",
        specs: [
          { label: "Peak Motor", value: "4000W", icon: "Zap" },
          { label: "Max Speed", value: "50 km/h", icon: "Gauge" },
          { label: "Max Range", value: "140 km", icon: "Route" },
          { label: "Battery", value: "1554Wh", icon: "Battery" },
          { label: "Tire Size", value: "18\"", icon: "Circle" },
          { label: "Weight", value: "25 kg", icon: "Weight" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Long Range", description: "140km range for extended rides" },
      { title: "Smart BMS", description: "Advanced battery protection" },
      { title: "UL 2272 Certified", description: "Safety certified" },
      { title: "Comfortable Ride", description: "Smooth 18-inch wheel experience" },
    ],
    availableUseCases: ["personal-mobility"],
  },
  // King Song 16X Series
  {
    id: "ks-16x-pro",
    name: "16X Pro",
    tagline: "Urban Performance King",
    description: "Premium 16-inch electric unicycle with 4600W motor and 1554Wh battery. 160km range and 50km/h speed make it the ultimate urban performance machine.",
    brand: "King Song",
    category: "personal-mobility",
    productType: "euc",
    powerType: "electric",
    image: "/products/kingsong/16x-pro.png",
    gallery: ["/products/kingsong/16x-pro.png"],
    colors: ["Black"],
    seoDescription: "King Song 16X Pro electric unicycle with 4600W motor, 1554Wh battery, and 160km range for urban performance.",
    seoKeywords: "King Song 16X Pro, electric unicycle, EUC, urban, performance, SDC Gulf",
    variants: [
      {
        id: "ks-16x-pro-standard",
        name: "Standard",
        priceIndicator: "premium",
        specs: [
          { label: "Peak Motor", value: "4600W", icon: "Zap" },
          { label: "Max Speed", value: "50 km/h", icon: "Gauge" },
          { label: "Max Range", value: "160 km", icon: "Route" },
          { label: "Battery", value: "1554Wh", icon: "Battery" },
          { label: "Tire Size", value: "16\"", icon: "Circle" },
          { label: "Rated Power", value: "2200W", icon: "Zap" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Performance Motor", description: "4600W peak power for acceleration" },
      { title: "Long Range", description: "160km maximum range" },
      { title: "UL 2272 Certified", description: "Safety certified" },
      { title: "App Control", description: "Full customization options" },
    ],
    availableUseCases: ["personal-mobility"],
  },
  {
    id: "ks-16x",
    name: "16X",
    tagline: "City Commuter Pro",
    description: "Versatile 16-inch electric unicycle with 2200W motor and 1554Wh battery. Outstanding 145km range at 50km/h speed for serious urban commuters.",
    brand: "King Song",
    category: "personal-mobility",
    productType: "euc",
    powerType: "electric",
    image: "/products/kingsong/16x.png",
    gallery: ["/products/kingsong/16x.png"],
    colors: ["Black"],
    seoDescription: "King Song 16X electric unicycle with 2200W motor, 1554Wh battery, and 145km range for urban commuting.",
    seoKeywords: "King Song 16X, electric unicycle, EUC, commuter, urban, SDC Gulf",
    variants: [
      {
        id: "ks-16x-standard",
        name: "Standard",
        priceIndicator: "standard",
        specs: [
          { label: "Peak Motor", value: "2200W", icon: "Zap" },
          { label: "Max Speed", value: "50 km/h", icon: "Gauge" },
          { label: "Max Range", value: "145 km", icon: "Route" },
          { label: "Battery", value: "1554Wh", icon: "Battery" },
          { label: "Tire Size", value: "16\"", icon: "Circle" },
          { label: "Weight", value: "23 kg", icon: "Weight" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Great Range", description: "145km for extended commutes" },
      { title: "Smart BMS", description: "Advanced battery protection" },
      { title: "UL 2272 Certified", description: "Safety certified" },
      { title: "Portable Size", description: "Compact 16-inch wheel" },
    ],
    availableUseCases: ["personal-mobility"],
  },
  // King Song 16S Series
  {
    id: "ks-16s-pro",
    name: "16S Pro",
    tagline: "Beginner's Best Choice",
    description: "Perfect entry-level 16-inch electric unicycle with 3800W motor and 840Wh battery. Designed specifically for beginners with gentle learning curve and safety features.",
    brand: "King Song",
    category: "personal-mobility",
    productType: "euc",
    powerType: "electric",
    image: "/products/kingsong/16s-pro.png",
    gallery: ["/products/kingsong/16s-pro.png"],
    colors: ["Black", "White"],
    seoDescription: "King Song 16S Pro beginner electric unicycle with 3800W motor, 840Wh battery, and safety features.",
    seoKeywords: "King Song 16S Pro, electric unicycle, EUC, beginner, entry-level, SDC Gulf",
    variants: [
      {
        id: "ks-16s-pro-standard",
        name: "Standard",
        priceIndicator: "standard",
        specs: [
          { label: "Peak Motor", value: "3800W", icon: "Zap" },
          { label: "Max Speed", value: "35 km/h", icon: "Gauge" },
          { label: "Max Range", value: "80 km", icon: "Route" },
          { label: "Battery", value: "840Wh", icon: "Battery" },
          { label: "Tire Size", value: "16\"", icon: "Circle" },
          { label: "Weight", value: "18 kg", icon: "Weight" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Beginner Friendly", description: "Gentle learning curve" },
      { title: "Smart BMS", description: "Battery protection system" },
      { title: "UL 2272 Certified", description: "Safety certified" },
      { title: "Lightweight", description: "Easy to carry at 18kg" },
    ],
    availableUseCases: ["personal-mobility"],
  },
  {
    id: "ks-16s",
    name: "16S",
    tagline: "Entry-Level Excellence",
    description: "Classic entry-level 16-inch electric unicycle with 1200W motor and 840Wh battery. Perfect for beginners looking to start their EUC journey.",
    brand: "King Song",
    category: "personal-mobility",
    productType: "euc",
    powerType: "electric",
    image: "/products/kingsong/16s.png",
    gallery: ["/products/kingsong/16s.png"],
    colors: ["Black", "White"],
    seoDescription: "King Song 16S entry-level electric unicycle with 1200W motor and 840Wh battery for beginners.",
    seoKeywords: "King Song 16S, electric unicycle, EUC, beginner, entry-level, SDC Gulf",
    variants: [
      {
        id: "ks-16s-standard",
        name: "Standard",
        priceIndicator: "standard",
        specs: [
          { label: "Peak Motor", value: "1200W", icon: "Zap" },
          { label: "Max Speed", value: "35 km/h", icon: "Gauge" },
          { label: "Max Range", value: "80 km", icon: "Route" },
          { label: "Battery", value: "840Wh", icon: "Battery" },
          { label: "Tire Size", value: "16\"", icon: "Circle" },
          { label: "Weight", value: "17 kg", icon: "Weight" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Perfect for Beginners", description: "Easy to learn and master" },
      { title: "Smart BMS", description: "Battery protection" },
      { title: "UL 2272 Certified", description: "Safety certified" },
      { title: "Lightweight Design", description: "Easy portability" },
    ],
    availableUseCases: ["personal-mobility"],
  },
  {
    id: "ks-s16-pro",
    name: "S16 Pro",
    tagline: "Compact Powerhouse",
    description: "Compact 16-inch electric unicycle with 3000W motor and 1480Wh Samsung battery. The perfect blend of portability and performance for urban riders.",
    brand: "King Song",
    category: "personal-mobility",
    productType: "euc",
    powerType: "electric",
    image: "/products/kingsong/s16-pro.png",
    gallery: ["/products/kingsong/s16-pro.png"],
    colors: ["Black", "White"],
    seoDescription: "King Song S16 Pro compact electric unicycle with 3000W motor and 1480Wh Samsung battery for urban mobility.",
    seoKeywords: "King Song S16 Pro, electric unicycle, EUC, compact, urban, SDC Gulf",
    variants: [
      {
        id: "ks-s16-pro-standard",
        name: "Standard",
        priceIndicator: "standard",
        specs: [
          { label: "Peak Motor", value: "3000W", icon: "Zap" },
          { label: "Max Speed", value: "50 km/h", icon: "Gauge" },
          { label: "Max Range", value: "100 km", icon: "Route" },
          { label: "Battery", value: "1480Wh Samsung", icon: "Battery" },
          { label: "Tire Size", value: "16\"", icon: "Circle" },
          { label: "Weight", value: "24 kg", icon: "Weight" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Compact Design", description: "16-inch wheel for easy portability" },
      { title: "Samsung Cells", description: "Premium Samsung 50S battery cells" },
      { title: "UL 2272 Certified", description: "Safety certified" },
      { title: "App Control", description: "Full feature control via app" },
    ],
    availableUseCases: ["personal-mobility"],
  },
  
  // King Song Electric Scooters
  {
    id: "ks-n16",
    name: "N16",
    tagline: "Flagship Dual Motor Scooter",
    description: "Premium 11-inch dual motor electric scooter with 3000W combined power and 1776Wh battery. Designed for serious riders who demand maximum performance and range.",
    brand: "King Song",
    category: "personal-mobility",
    productType: "scooter",
    powerType: "electric",
    image: "/products/kingsong/n16.png",
    gallery: ["/products/kingsong/n16.png"],
    colors: ["Black"],
    seoDescription: "King Song N16 flagship electric scooter with dual 1500W motors, 1776Wh battery, and 90km range.",
    seoKeywords: "King Song N16, electric scooter, dual motor, long range, personal mobility, SDC Gulf",
    variants: [
      {
        id: "ks-n16-standard",
        name: "Standard",
        priceIndicator: "premium",
        specs: [
          { label: "Motor Power", value: "1500W×2", icon: "Zap" },
          { label: "Max Speed", value: "70 km/h", icon: "Gauge" },
          { label: "Max Range", value: "90 km", icon: "Route" },
          { label: "Battery", value: "1776Wh", icon: "Battery" },
          { label: "Tire Size", value: "11\"", icon: "Circle" },
          { label: "Max Load", value: "120 kg", icon: "Weight" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Dual Motor", description: "Twin 1500W motors for incredible acceleration" },
      { title: "NFC Display", description: "Smart NFC-enabled display panel" },
      { title: "UL 2272 Certified", description: "Meets highest safety standards" },
      { title: "Hydraulic Brakes", description: "Professional-grade stopping power" },
    ],
    availableUseCases: ["personal-mobility"],
  },
  {
    id: "ks-n12-pro",
    name: "N12 Pro",
    tagline: "Performance Commuter",
    description: "High-performance 10-inch electric scooter with 1400W peak motor and 858Wh battery. Ideal for daily commuting with impressive 80km range.",
    brand: "King Song",
    category: "personal-mobility",
    productType: "scooter",
    powerType: "electric",
    image: "/products/kingsong/n12-pro.png",
    gallery: ["/products/kingsong/n12-pro.png"],
    colors: ["Black"],
    seoDescription: "King Song N12 Pro electric scooter with 1400W peak motor, 858Wh battery, and 80km range for daily commuting.",
    seoKeywords: "King Song N12 Pro, electric scooter, commuter, long range, SDC Gulf",
    variants: [
      {
        id: "ks-n12-pro-standard",
        name: "Standard",
        priceIndicator: "standard",
        specs: [
          { label: "Peak Motor", value: "1400W", icon: "Zap" },
          { label: "Max Speed", value: "50 km/h", icon: "Gauge" },
          { label: "Max Range", value: "80 km", icon: "Route" },
          { label: "Battery", value: "858Wh", icon: "Battery" },
          { label: "Tire Size", value: "10\"", icon: "Circle" },
          { label: "Max Load", value: "120 kg", icon: "Weight" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Powerful Motor", description: "1400W peak for hill climbing" },
      { title: "Long Range", description: "80km on single charge" },
      { title: "UL 2272 Certified", description: "Safety certified" },
      { title: "Disc Brakes", description: "Reliable stopping power" },
    ],
    availableUseCases: ["personal-mobility"],
  },
  {
    id: "ks-n15",
    name: "N15 Series",
    tagline: "Balanced Performance",
    description: "Versatile 10-inch electric scooter with 900W peak motor and 697Wh battery. Perfect balance of performance, range, and value for everyday riders.",
    brand: "King Song",
    category: "personal-mobility",
    productType: "scooter",
    powerType: "electric",
    image: "/products/kingsong/n15.png",
    gallery: ["/products/kingsong/n15.png"],
    colors: ["Black"],
    seoDescription: "King Song N15 Series electric scooter with 900W peak motor, 697Wh battery, and 60km range.",
    seoKeywords: "King Song N15, electric scooter, balanced, daily commute, SDC Gulf",
    variants: [
      {
        id: "ks-n15-standard",
        name: "Standard",
        priceIndicator: "standard",
        specs: [
          { label: "Peak Motor", value: "900W", icon: "Zap" },
          { label: "Max Speed", value: "40 km/h", icon: "Gauge" },
          { label: "Max Range", value: "60 km", icon: "Route" },
          { label: "Battery", value: "697Wh", icon: "Battery" },
          { label: "Tire Size", value: "10\"", icon: "Circle" },
          { label: "Max Load", value: "100 kg", icon: "Weight" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Balanced Design", description: "Perfect for daily commuting" },
      { title: "Good Range", description: "60km comfortable range" },
      { title: "UL 2272 Certified", description: "Safety certified" },
      { title: "Easy Folding", description: "Quick fold mechanism" },
    ],
    availableUseCases: ["personal-mobility"],
  },
  
  // King Song Electric Bikes
  {
    id: "ks-m26-pro",
    name: "M26 Pro",
    tagline: "Ultimate E-Bike Power",
    description: "Powerful 20-inch electric bike with 1000W motor and 1030Wh battery. Reach speeds of 50km/h with an impressive 70km range for serious off-road and commuting.",
    brand: "King Song",
    category: "personal-mobility",
    productType: "ebike",
    powerType: "electric",
    image: "/products/kingsong/m26-pro.png",
    gallery: ["/products/kingsong/m26-pro.png"],
    colors: ["Black", "Orange"],
    seoDescription: "King Song M26 Pro electric bike with 1000W motor, 1030Wh battery, 50km/h speed, and 70km range.",
    seoKeywords: "King Song M26 Pro, electric bike, ebike, powerful, SDC Gulf",
    variants: [
      {
        id: "ks-m26-pro-standard",
        name: "Standard",
        priceIndicator: "premium",
        specs: [
          { label: "Motor Power", value: "1000W", icon: "Zap" },
          { label: "Max Speed", value: "50 km/h", icon: "Gauge" },
          { label: "Max Range", value: "70 km", icon: "Route" },
          { label: "Battery", value: "1030Wh", icon: "Battery" },
          { label: "Tire Size", value: "20\"", icon: "Circle" },
          { label: "Max Load", value: "150 kg", icon: "Weight" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Powerful Motor", description: "1000W for hill climbing and speed" },
      { title: "Fat Tires", description: "20-inch fat tires for any terrain" },
      { title: "UL 2272 Certified", description: "Safety certified" },
      { title: "LCD Display", description: "Full-featured digital display" },
    ],
    availableUseCases: ["personal-mobility"],
  },
  {
    id: "ks-m22-pro",
    name: "M22 Pro",
    tagline: "Long-Range E-Bike Champion",
    description: "Premium 20-inch electric bike with 1300W motor and massive 1716Wh battery. Exceptional 180km range makes it perfect for long-distance adventures.",
    brand: "King Song",
    category: "personal-mobility",
    productType: "ebike",
    powerType: "electric",
    image: "/products/kingsong/m22-pro.png",
    gallery: ["/products/kingsong/m22-pro.png"],
    colors: ["Black", "Orange"],
    seoDescription: "King Song M22 Pro electric bike with 1300W motor, 1716Wh battery, and incredible 180km range.",
    seoKeywords: "King Song M22 Pro, electric bike, ebike, long range, SDC Gulf",
    variants: [
      {
        id: "ks-m22-pro-standard",
        name: "Standard",
        priceIndicator: "premium",
        specs: [
          { label: "Motor Power", value: "1300W", icon: "Zap" },
          { label: "Max Speed", value: "50 km/h", icon: "Gauge" },
          { label: "Max Range", value: "180 km", icon: "Route" },
          { label: "Battery", value: "1716Wh", icon: "Battery" },
          { label: "Tire Size", value: "20\"", icon: "Circle" },
          { label: "Max Load", value: "150 kg", icon: "Weight" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Incredible Range", description: "180km for all-day riding" },
      { title: "Powerful Motor", description: "1300W for any terrain" },
      { title: "UL 2272 Certified", description: "Safety certified" },
      { title: "Fat Tires", description: "All-terrain capability" },
    ],
    availableUseCases: ["personal-mobility"],
  },
  {
    id: "ks-m3",
    name: "M3",
    tagline: "Urban Commuter E-Bike",
    description: "Sleek 20-inch electric bike with 350W motor and 375Wh battery. Perfect for urban commuting with 50km range and 35km/h speed.",
    brand: "King Song",
    category: "personal-mobility",
    productType: "ebike",
    powerType: "electric",
    image: "/products/kingsong/m3.png",
    gallery: ["/products/kingsong/m3.png"],
    colors: ["Black", "Blue", "Green"],
    seoDescription: "King Song M3 urban electric bike with 350W motor, 375Wh battery, and 50km range for city commuting.",
    seoKeywords: "King Song M3, electric bike, ebike, urban, commuter, SDC Gulf",
    variants: [
      {
        id: "ks-m3-standard",
        name: "Standard",
        priceIndicator: "standard",
        specs: [
          { label: "Motor Power", value: "350W", icon: "Zap" },
          { label: "Max Speed", value: "35 km/h", icon: "Gauge" },
          { label: "Max Range", value: "50 km", icon: "Route" },
          { label: "Battery", value: "375Wh", icon: "Battery" },
          { label: "Tire Size", value: "20\"", icon: "Circle" },
          { label: "Max Load", value: "120 kg", icon: "Weight" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Urban Design", description: "Sleek and practical for city use" },
      { title: "Efficient Motor", description: "350W for smooth riding" },
      { title: "UL 2272 Certified", description: "Safety certified" },
      { title: "Easy Folding", description: "Compact storage" },
    ],
    availableUseCases: ["personal-mobility"],
  },
  {
    id: "ks-m2",
    name: "M2",
    tagline: "Compact City Cruiser",
    description: "Compact 16-inch electric bike with 500W motor and 288Wh battery. Lightweight and portable design perfect for urban environments and short commutes.",
    brand: "King Song",
    category: "personal-mobility",
    productType: "ebike",
    powerType: "electric",
    image: "/products/kingsong/m2.png",
    gallery: ["/products/kingsong/m2.png"],
    colors: ["Black", "Pink"],
    seoDescription: "King Song M2 compact electric bike with 500W motor, 288Wh battery, and 30km range for city cruising.",
    seoKeywords: "King Song M2, electric bike, ebike, compact, city, SDC Gulf",
    variants: [
      {
        id: "ks-m2-standard",
        name: "Standard",
        priceIndicator: "standard",
        specs: [
          { label: "Motor Power", value: "500W", icon: "Zap" },
          { label: "Max Speed", value: "25 km/h", icon: "Gauge" },
          { label: "Max Range", value: "30 km", icon: "Route" },
          { label: "Battery", value: "288Wh", icon: "Battery" },
          { label: "Tire Size", value: "16\"", icon: "Circle" },
          { label: "Max Load", value: "100 kg", icon: "Weight" },
        ],
      },
    ],
    commonFeatures: [
      { title: "Compact Size", description: "16-inch wheels for easy storage" },
      { title: "Lightweight", description: "Easy to carry and transport" },
      { title: "UL 2272 Certified", description: "Safety certified" },
      { title: "Quick Charge", description: "Fast charging capability" },
    ],
    availableUseCases: ["personal-mobility"],
  },
];

export const useCasePackages: UseCasePackage[] = [
  {
    id: "police-patrol-kit",
    name: "Police Patrol Kit",
    useCase: "security-patrol",
    applicableProducts: ["itank", "itango", "adv-150cc"],
    equipment: [
      { name: "PA System", description: "200W powerful speaker with multiple siren tones", icon: "Volume2" },
      { name: "Emergency Lighting", description: "High-visibility LED warning lights and strobes", icon: "Siren" },
      { name: "Rugged Tablet Mount", description: "RAM mounting for Panasonic FZ-G2 rugged computer", icon: "Tablet" },
      { name: "Storage Compartments", description: "Secure compartments for patrol equipment and gear", icon: "Package" },
    ],
    additionalSpecs: [
      { label: "PA Power", value: "200W", icon: "Volume2" },
      { label: "Tablet", value: "Panasonic FZ-G2", icon: "Tablet" },
      { label: "Weather Rating", value: "IP66", icon: "CloudRain" },
    ],
  },
  {
    id: "medical-response-kit",
    name: "Medical Response Kit",
    useCase: "medical-emergency",
    applicableProducts: ["itank"],
    equipment: [
      { name: "First Aid Storage", description: "Temperature-controlled medical supply compartment", icon: "HeartPulse" },
      { name: "Emergency Beacon", description: "360° visibility warning lights for rapid response", icon: "Siren" },
      { name: "Communication System", description: "Integrated radio communication for dispatch", icon: "Radio" },
      { name: "Stretcher Mount", description: "Quick-release attachment for portable stretcher", icon: "Bed" },
    ],
    additionalSpecs: [
      { label: "Storage Temp", value: "2-8°C", icon: "Thermometer" },
      { label: "Beacon Range", value: "500m", icon: "Eye" },
      { label: "Radio Range", value: "5 km", icon: "Radio" },
    ],
  },
  {
    id: "logistics-kit",
    name: "Logistics Kit",
    useCase: "logistics-utility",
    applicableProducts: ["itank", "itango", "icart"],
    equipment: [
      { name: "Cargo Box", description: "Weatherproof rear cargo container with 100L capacity", icon: "Package" },
      { name: "Package Scanner", description: "Integrated barcode scanner for delivery tracking", icon: "Scan" },
      { name: "GPS Tracking", description: "Real-time fleet tracking and route optimization", icon: "MapPin" },
    ],
    additionalSpecs: [
      { label: "Cargo Capacity", value: "100L", icon: "Package" },
      { label: "Max Payload", value: "50 kg", icon: "Weight" },
    ],
  },
  {
    id: "hospitality-kit",
    name: "Hospitality Kit",
    useCase: "events-hospitality",
    applicableProducts: ["ilark", "icart"],
    equipment: [
      { name: "Premium Seating", description: "Upgraded comfort seat with premium cushioning", icon: "Armchair" },
      { name: "Phone Holder", description: "Universal mount for navigation and communication", icon: "Smartphone" },
      { name: "Basket Attachment", description: "Front basket for guest items and supplies", icon: "ShoppingBasket" },
    ],
    additionalSpecs: [
      { label: "Seat Comfort", value: "Premium", icon: "Armchair" },
    ],
  },
];

// Helper functions
export const getProductLinesByUseCase = (useCaseId: string): ProductLine[] => {
  return productLines.filter((pl) => pl.availableUseCases.includes(useCaseId));
};

export const getProductLineById = (productId: string): ProductLine | undefined => {
  return productLines.find((pl) => pl.id === productId);
};

export const getPackagesForUseCase = (useCaseId: string): UseCasePackage[] => {
  return useCasePackages.filter((pkg) => pkg.useCase === useCaseId);
};

export const getPackagesForProduct = (productId: string): UseCasePackage[] => {
  return useCasePackages.filter((pkg) => pkg.applicableProducts.includes(productId));
};

export const getCategoryById = (categoryId: string): ProductCategory | undefined => {
  return productCategories.find((cat) => cat.id === categoryId);
};

// Get commercial/B2B products (Doohan - excludes XSTO and customized)
export const getCommercialProducts = (): ProductLine[] => {
  return productLines.filter((pl) => pl.category === "commercial" && !pl.customized);
};

// Get customized products
export const getCustomizedProducts = (): ProductLine[] => {
  return productLines.filter((pl) => pl.customized);
};

// Get industrial equipment products (XSTO)
export const getIndustrialProducts = (): ProductLine[] => {
  return productLines.filter((pl) => pl.category === "industrial");
};

// Get personal mobility products (King Song)
export const getPersonalMobilityProducts = (): ProductLine[] => {
  return productLines.filter((pl) => pl.category === "personal-mobility");
};

// Get all products
export const getAllProducts = (): ProductLine[] => {
  return productLines;
};

// Legacy helper - get standard products (excluding ADV 150CC which is custom-only)
export const getStandardProducts = (): ProductLine[] => {
  return productLines.filter((pl) => ["itank", "itango", "ilark", "icart"].includes(pl.id));
};

// Get products by type (for filter UI)
export const getProductsByType = (productType: string): ProductLine[] => {
  if (productType === "all") {
    return getPersonalMobilityProducts();
  }
  return productLines.filter((pl) => pl.productType === productType && pl.category === "personal-mobility");
};

// Get all EUCs
export const getElectricUnicycles = (): ProductLine[] => {
  return productLines.filter((pl) => pl.productType === "euc");
};

// Get all electric scooters (King Song)
export const getKingSongScooters = (): ProductLine[] => {
  return productLines.filter((pl) => pl.productType === "scooter" && pl.brand === "King Song");
};

// Get all e-bikes
export const getElectricBikes = (): ProductLine[] => {
  return productLines.filter((pl) => pl.productType === "ebike");
};
